import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { todoApi } from '../api/todoApi';
import { userApi } from '../api/userApi';
import { CreateTodoRequest, UpdateTodoRequest, TodoItem, TodoList } from '../types/todo';
import { useUser } from '../contexts/UserContext';
import { notifications } from '@mantine/notifications';

export const useTodos = () => {
  const { currentUser } = useUser();
  
  return useQuery({
    queryKey: ['todos', currentUser?.email],
    queryFn: () => api.getAllTodos(currentUser?.email || ''),
    enabled: !!currentUser?.email,
  });
};

export const useTodo = (id: number) => {
  const { currentUser } = useUser();
  
  return useQuery({
    queryKey: ['todos', id],
    queryFn: () => api.getTodo(id, currentUser?.email || ''),
    enabled: !!id && !!currentUser?.email,
  });
};

export const useCreateTodo = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useUser();
  return useMutation({
    mutationFn: async (data: CreateTodoRequest) => {
      const userEmail = currentUser?.email || '';
      console.log('[useCreateTodo] Starting todo creation:', { data, userEmail });
      
      // If no listId is provided, ensure we have a list to use
      if (!data.listId) {
        console.log('[useCreateTodo] No listId provided, checking for existing lists...');
        // Check backend for existing lists (not cache, which may be stale for new users)
        const existingLists = await api.getAllLists(userEmail);
        console.log('[useCreateTodo] Existing lists from backend:', existingLists);
        
        if (existingLists.length === 0) {
          // No lists exist, create a default one first
          console.log('[useCreateTodo] No lists found in backend, creating default list...');
          try {
            const defaultList = await api.createList({
              name: "My Tasks",
              description: "Default task list"
            }, userEmail);
            console.log('[useCreateTodo] Default list created successfully:', defaultList);
            
            // Immediately update the cache with the new list
            queryClient.setQueryData(['todolists', userEmail], [defaultList]);
            console.log('[useCreateTodo] Cache updated with new list');
            
            // Use the new list ID
            data.listId = defaultList.id;
            console.log('[useCreateTodo] Using new listId:', data.listId);
          } catch (error) {
            console.error('[useCreateTodo] Failed to create default list:', error);
            throw error;
          }
        } else {
          // Use the default list if it exists, otherwise use the first available list
          const defaultList = existingLists.find((list: TodoList) => list.isDefault);
          const targetList = defaultList || existingLists[0];
          console.log('[useCreateTodo] Using existing list:', targetList, defaultList ? '(default list)' : '(first list)');
          queryClient.setQueryData(['todolists', userEmail], existingLists);
          data.listId = targetList.id;
        }
      } else {
        console.log('[useCreateTodo] ListId already provided:', data.listId);
      }
      
      console.log('[useCreateTodo] Creating todo with listId:', data.listId);
      const result = await api.createTodo(data, userEmail);
      console.log('[useCreateTodo] Todo created successfully:', result);
      return result;
    },
    // Optimistic update for adding a todo
    onMutate: async (data: CreateTodoRequest) => {
      await queryClient.cancelQueries({ queryKey: ['todos', currentUser?.email] });
      const previousTodos = queryClient.getQueryData<TodoItem[]>(['todos', currentUser?.email]);
      
      // Get todo lists to find the list name
      const todoLists = queryClient.getQueryData<TodoList[]>(['todolists', currentUser?.email]);
      const targetList = todoLists?.find(list => list.id === data.listId);
      
      // Generate a temporary optimistic todo
      const optimisticTodo: TodoItem = {
        ...data,
        id: -Date.now(), // Use negative ID to avoid conflicts with real IDs
        isCompleted: false,
        createdAt: new Date().toISOString(),
        createdBy: data.createdBy || currentUser?.email || '',
        listId: data.listId || (todoLists?.[0]?.id ?? 0),
        listName: targetList?.name || todoLists?.[0]?.name || 'Default List',
        comments: [],
        rank: Date.now(), // temp rank
        status: data.status, // Preserve status for Kanban
        completedAt: undefined,
        assignedTo: data.assignedTo || undefined,
        priority: data.priority || 'Medium',
        dueDate: data.dueDate || undefined,
        reminderDateTime: data.reminderDateTime || undefined,
        tags: data.tags || undefined,
        description: data.description || undefined,
        subTasks: [],
        completedSubTasksCount: 0,
        totalSubTasksCount: 0,
      };
      if (previousTodos) {
        queryClient.setQueryData(['todos', currentUser?.email], [...previousTodos, optimisticTodo]);
      }
      return { previousTodos };
    },
    onError: (err, _data, context) => {
      console.error('[useCreateTodo] Error creating todo:', err);
      // Rollback on error
      if (context?.previousTodos) {
        queryClient.setQueryData(['todos', currentUser?.email], context.previousTodos);
      }
      // Show error notification
      notifications.show({
        title: 'Failed to create todo',
        message: err instanceof Error ? err.message : 'An unknown error occurred',
        color: 'red',
      });
    },
    onSuccess: (newTodo, variables) => {
      console.log('[useCreateTodo] onSuccess - updating cache with new todo:', newTodo);
      queryClient.setQueryData(['todos', currentUser?.email], (old: TodoItem[] | undefined) => {
        if (!old) return [newTodo];
        // Replace the optimistic todo with the real one from the server
        const optimisticTodoIndex = old.findIndex(todo => todo.id < 0 && todo.title === variables.title);
        if (optimisticTodoIndex > -1) {
          const newTodos = [...old];
          newTodos[optimisticTodoIndex] = newTodo;
          return newTodos;
        }
        // Fallback if optimistic todo not found, just add the new one
        return [newTodo, ...old];
      });
    },
    onSettled: async () => {
      console.log('[useCreateTodo] Mutation settled, refetching queries to ensure UI updates');
      // Force refetch both lists and todos to ensure UI is in sync
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['todolists', currentUser?.email] }),
        queryClient.refetchQueries({ queryKey: ['todos', currentUser?.email] })
      ]);
      console.log('[useCreateTodo] Queries refetched successfully');
    },
  });
};

export const useUpdateTodo = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useUser();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateTodoRequest }) => 
      api.updateTodo(id, data, currentUser?.email || ''),
    // Optimistic update for editing a todo
    onMutate: async ({ id, data }: { id: number; data: UpdateTodoRequest }) => {
      await queryClient.cancelQueries({ queryKey: ['todos', currentUser?.email] });
      const previousTodos = queryClient.getQueryData<TodoItem[]>(['todos', currentUser?.email]);
      if (previousTodos) {
        const newTodos = previousTodos.map(todo =>
          todo.id === id ? { ...todo, ...data, updatedAt: new Date().toISOString() } : todo
        );
        queryClient.setQueryData(['todos', currentUser?.email], newTodos);
      }
      return { previousTodos };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousTodos) {
        queryClient.setQueryData(['todos', currentUser?.email], context.previousTodos);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['todos', currentUser?.email] });
      queryClient.invalidateQueries({ queryKey: ['todolists', currentUser?.email] });
    },
  });
};

export const useDeleteTodo = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useUser();
  return useMutation({
    mutationFn: (id: number) => api.deleteTodo(id),
    // Optimistic update for deleting a todo
    onMutate: async (id: number) => {
      await queryClient.cancelQueries({ queryKey: ['todos', currentUser?.email] });
      const previousTodos = queryClient.getQueryData<TodoItem[]>(['todos', currentUser?.email]);
      if (previousTodos) {
        const newTodos = previousTodos.filter(todo => todo.id !== id);
        queryClient.setQueryData(['todos', currentUser?.email], newTodos);
      }
      return { previousTodos };
    },
    onError: (_err, _id, context) => {
      if (context?.previousTodos) {
        queryClient.setQueryData(['todos', currentUser?.email], context.previousTodos);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['todos', currentUser?.email] });
      queryClient.invalidateQueries({ queryKey: ['todolists', currentUser?.email] });
    },
  });
};

export const useToggleTodo = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useUser();

  return useMutation({
    mutationFn: (id: number) => api.toggleTodo(id, currentUser?.email || ''),
    retry: 2, // Retry twice on failure (total of 3 attempts)
    retryDelay: 1000, // Wait 1 second between retries
    
    // Optimistic update
    onMutate: async (id: number) => {
      await queryClient.cancelQueries({ queryKey: ['todos', currentUser?.email] });
      const previousTodos = queryClient.getQueryData<TodoItem[]>(['todos', currentUser?.email]);
      if (previousTodos) {
        // Toggle the completed state optimistically
        const newTodos = previousTodos.map(todo =>
          todo.id === id ? { 
            ...todo, 
            isCompleted: !todo.isCompleted,
            completedAt: !todo.isCompleted ? new Date().toISOString() : null
          } : todo
        );
        queryClient.setQueryData(['todos', currentUser?.email], newTodos);
      }
      return { previousTodos };
    },
    onError: (error, _id, context) => {
      // Rollback on error
      if (context?.previousTodos) {
        queryClient.setQueryData(['todos', currentUser?.email], context.previousTodos);
      }
      
      // Show error notification to user
      notifications.show({
        title: 'Failed to update task',
        message: error instanceof Error ? error.message : 'Please check your connection and try again',
        color: 'red',
        autoClose: 5000
      });
      
      console.error('Toggle todo failed:', error);
    },
    onSuccess: (updatedTodo, id) => {
      // Update cache with server response to ensure consistency
      queryClient.setQueryData(['todos', currentUser?.email], (old: TodoItem[] | undefined) => {
        if (!old) return [updatedTodo];
        return old.map(todo => todo.id === id ? updatedTodo : todo);
      });
    },
    onSettled: (_data, error) => {
      // Only invalidate on error (success already updated cache)
      if (error) {
        queryClient.invalidateQueries({ queryKey: ['todos', currentUser?.email] });
        queryClient.invalidateQueries({ queryKey: ['todolists', currentUser?.email] });
      }
    },
  });
};

// Todo Lists hooks
export const useTodoLists = () => {
  const { currentUser } = useUser();
  
  return useQuery({
    queryKey: ['todolists', currentUser?.email],
    queryFn: () => api.getAllLists(currentUser?.email || ''),
    enabled: !!currentUser?.email,
  });
};

export const useTodoList = (id: number) => {
  const { currentUser } = useUser();
  
  return useQuery({
    queryKey: ['todolists', id, currentUser?.email],
    queryFn: () => {
      if (!currentUser?.email) return null;
      return todoApi.getListWithUserEmail(id, currentUser.email);
    },
    enabled: !!id && !!currentUser?.email,
  });
};

// Hook to ensure a default list exists
export const useEnsureDefaultList = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useUser();
  
  return useMutation({
    mutationFn: async () => {
      const userEmail = currentUser?.email || '';
      const lists = await api.getAllLists(userEmail);
      if (lists.length === 0) {
        return api.createList({
          name: "My Todos",
          description: "Default todo list"
        }, userEmail);
      }
      return lists[0];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todolists'] });
    },
  });
};

export const useUsers = () => {
  const { currentUser } = useUser();
  
  return useQuery({
    queryKey: ['users', currentUser?.email],
    queryFn: () => userApi.getContacts(),
    enabled: !!currentUser?.email,
  });
};
